import { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { CONST_SPAPI_SRC_ONBOARDING_FOR_URI_EXTERNAL } from "utils/getUrlPrefix";
import TrellisPanelSignUp from "./TrellisPanelSignUp";
import SignupLayout from "views/onboarding/layout/SignupLayout";
import ExtensionSignUpMarketingPanel from "./ExtensionSignUpMarketingPanel";
import SignUpForm from "../../../modules/auth/SignUpForm";
import Login from "views/Login";
import { Flex, Grid } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import type { RootState } from "../../../redux/store";

class SingleSignIn extends Component<
  PropsFromRedux & RouteComponentProps,
  {
    signUpPage: boolean;
    extensionSignup: boolean;
  }
> {
  constructor(props: PropsFromRedux & RouteComponentProps) {
    super(props);
    this.state = {
      signUpPage: false,
      extensionSignup: false,
    };
  }

  componentDidMount() {
    if (this.props.auth.isAuthenticated) {
      // handle redirect from SP-API Auth
      if (window.location.href.includes("spapi_oauth_code")) {
        const queryParams = new URLSearchParams(window.location.search);
        const spapi_oauth_code = queryParams.get("spapi_oauth_code");
        const selling_partner_id = queryParams.get("selling_partner_id");
        const stateEncoded = queryParams.get("state");
        const decodedState = atob(stateEncoded!);
        const stateJson = JSON.parse(decodedState) as { region: string; src: string };
        const region_encoded = stateJson["region"];
        const spapisrc_encoded = stateJson["src"];
        const spapi_request_state = {
          spapi_oauth_code: spapi_oauth_code,
          selling_partner_id: selling_partner_id,
          region_encoded: region_encoded,
        };

        const usePathname =
          spapisrc_encoded === CONST_SPAPI_SRC_ONBOARDING_FOR_URI_EXTERNAL ? "/onboard/amzspapi" : "/user/amzsummary";

        this.props.history.push({
          pathname: usePathname,
          search: "?edit_sp_api=true",
          state: spapi_request_state,
        });
      } else {
        this.props.history.push("/onboard");
      }
    }

    // Set extension user flag in localStorage for GA tracking
    if (this.props.history.location.search.includes("source=extension")) {
      localStorage.extensionSignup = new Date().toISOString().split("T")[0];
      this.setState({ extensionSignup: true });
    }

    if (window.location.href.includes("/signup")) {
      this.setOnboardingHeaders();

      if (this.props.history.location.search.includes("onboard=v2")) {
        this.props.history.push("/?onboard=v2");
      } else {
        this.props.history.push("/");
      }
      this.setState({ signUpPage: true });
    } else if (window.location.href.includes("/referral/") || window.location.href.includes("/affiliate")) {
      this.setState({ signUpPage: true });
    }
  }

  componentDidUpdate() {
    if (this.props.auth.isAuthenticated) {
      this.props.history.push("/onboard");
    }

    this.setOnboardingHeaders();
  }

  setOnboardingHeaders = () => {
    // Add new signup panel SEO
    const documentTitle = document.head.querySelector("title")!;
    documentTitle.innerText = "Sign Up For Trellis";

    const descriptionMeta = document.createElement("meta");
    descriptionMeta.setAttribute("name", "description");
    descriptionMeta.setAttribute(
      "content",
      "Automate your Amazon ads with Trellis. Increase your sales while saving time and money.",
    );

    document.head.appendChild(descriptionMeta);
  };

  handleScrollClick = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  render() {
    const { bypass_onboarding } = this.props.user;
    const { amazon_spapi_access, eu_amazon_spapi_access, fe_amazon_spapi_access } = this.props.amz_onboard;
    if (
      bypass_onboarding &&
      [amazon_spapi_access, eu_amazon_spapi_access, fe_amazon_spapi_access].includes("in_progress")
    ) {
      return <SignupLayout placeholderLayout={true} />;
    }

    return (
      <>
        {this.state.signUpPage ?
          <Grid className={css({ maxWidth: "100vw", background: "#FFF" })} columns={{ base: 1, lg: 2 }}>
            <Flex
              className={css({
                display: { base: "none", md: "block" },
                alignItems: "center",
                height: "100vh",
              })}
            >
              {this.state.extensionSignup ?
                <ExtensionSignUpMarketingPanel />
              : <TrellisPanelSignUp alt={4} handleScrollClick={this.handleScrollClick} style={{ height: "100%" }} />}
            </Flex>
            <SignUpForm
              extensionSignup={this.state.extensionSignup}
              onLoginClick={() => {
                this.setState({
                  signUpPage: false,
                });
              }}
            />
          </Grid>
        : <Login
            onSignUpClick={() => {
              this.setState({ signUpPage: true });
              this.setOnboardingHeaders();
            }}
          />
        }
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  user: state.user,
  amz_onboard: state.amz_onboard,
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withRouter(SingleSignIn));
